import React, { FC, useContext } from 'react';
import styled from 'styled-components';

import ChatCampaignActions from './ChatCampaignActions';
import ChatCampaignTriggers from './ChatCampaignTriggers';
import Footer from 'src/presentation/components/forms/Footer';
import Form, { FormEditorProps, useForm, useStandardRepoOnSave } from 'src/presentation/components/forms/Form';
import InputField, { InputType } from 'src/presentation/components/forms/InputField';
import TextareaField from 'src/presentation/components/forms/TextareaField';
import chatCampaignsResource from 'src/actions/chatCampaigns/chatCampaignsResource';
import generateId from 'src/lib/generateId';
import { Body, PageHeader, Row } from 'src/presentation/components/forms/Layout';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { ChatCampaignContext } from './ChatCampaignConfiguration';
import { ChatCampaignStatus, ChatCampaignActionType } from 'src/models/ChatCampaigns/ChatCampaign';
import { helpDocsRender } from './ChatCampaignConfiguration';

const ChatCampaignEditor: FC<FormEditorProps> = ({ errors, item, root }) => {
  const { add, update, validate } = chatCampaignsResource.effects;
  const { resetErrors } = chatCampaignsResource.actions;

  const { chatCampaignsList } = useContext(ChatCampaignContext);

  const saveHandler = useStandardRepoOnSave();
  const formContext = useForm({
    add,
    defaultItem: {
      actions: [
        {
          id: generateId(),
          type: ChatCampaignActionType.GREETING,
          content: {
            message: '',
            ignoreThrottling: false,
            skipOnboarding: false,
          },
        },
      ],
      createdAt: new Date().toISOString(),
      status: ChatCampaignStatus.ENABLED,
      priority: chatCampaignsList.length + 1,
      triggers: [],
    },
    errors,
    item,
    resetErrors,
    root,
    onSave: saveHandler,
    update,
    validate,
  });

  const { isNew, onClose, onSave } = formContext;

  return (
    <>
      <Form context={formContext} preventSaveOnEnter>
        <PageHeader
          headerText={`${isNew ? 'Create' : 'Edit'} a Campaign`}
          descriptionText={
            <>To identify which campaign will work best for your customers, visit our {helpDocsRender}.</>
          }
        />
        <StyledBody>
          <Row>
            <InputField
              label="Name"
              description="Agents will see this campaign name alongside responses from customers, in
the conversation timeline."
              data-aid="name"
              attr="name"
              placeholder="Name a campaign"
            />
          </Row>
          <Row>
            <TextareaField
              attr="description"
              data-aid="description"
              label="Description"
              description="Agents will see the description in the conversation timeline. Be descriptive to give agents context as to why they were routed a customer."
              placeholder="Describe what the campaign is for and what it should do"
            />
          </Row>
          <PriorityRow>
            <InputField
              attr="priority"
              data-aid="priority"
              type={InputType.number}
              label="Order"
              description="Set the order of priority triggers are executed to display a Campaign.
If you have multiple Triggers or Campaigns for the same web page, then all of its Conditions must be met
for a Campaign to launch. When utilizing the URL condition, do not include the URL protocol (e.g., https:// or http://)."
            />
          </PriorityRow>

          <ChatCampaignTriggers attr="triggers" data-aid="triggers" errors={errors} />
          <ChatCampaignActions attr="actions" data-aid="actions" />
        </StyledBody>

        <Footer>
          <Button data-aid="cancel-button" type={Button.Types.TEXT} onClick={onClose}>
            Cancel
          </Button>
          <Button data-aid="save-button" onClick={onSave}>
            Save
          </Button>
        </Footer>
      </Form>
    </>
  );
};

const StyledBody = styled(Body)`
  max-width: 546px;
`;

const PriorityRow = styled(Row)`
  input {
    max-width: 72px;
  }
`;

export default ChatCampaignEditor;
